'use client';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import type {NavContent, SiteBranding} from '@olympus-frontend/tenant-config';
import type {ElementType} from 'react';
import {trackHeaderRegisterInterestClick} from '../../analytics';
import {LOCALES, type Locale} from '../../shared/constants/locales';
import {RegisterInterestCta} from '../register-interest';
import {BrandLogo} from './BrandLogo';
import {ContactLinks} from './ContactLinks';
import {DEVELOPMENTS_NAV_KEY, DestinationsMenu, type DestinationsMenuData} from './DestinationsMenu';
import {DESKTOP_BAR_HEIGHT} from './layout';
import {LocaleSwitcher} from './LocaleSwitcher';
import {NavItem} from './NavItem';

const LOGO_WIDTH = 109;
const LOGO_HEIGHT = 36;

const LOGO_HEIGHT_BY_LOCALE = {[LOCALES.ARABIC]: 50};

const DesktopBar = styled(Box)(({theme}) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    // Figma node 547-30947: 96px bar around an 80px nav row, i.e. 8px block
    // padding. minHeight is border-box, so it has to carry the padding too.
    paddingBlock: theme.spacing(1),
    minHeight: theme.spacing(DESKTOP_BAR_HEIGHT),
  },
}));

const DesktopNav = styled(Box)<{component?: ElementType}>(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
}));

const RightSlot = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
}));

interface HeaderDesktopProps {
  branding: SiteBranding;
  content: NavContent;
  locale: Locale;
  locales: readonly Locale[];
  hotline?: string;
  whatsappHref?: string;
  developments?: DestinationsMenuData;
  showDevelopments: boolean;
}

export function HeaderDesktop({
  branding,
  content,
  locale,
  locales,
  hotline,
  whatsappHref,
  developments,
  showDevelopments,
}: HeaderDesktopProps) {
  return (
    <DesktopBar>
      <BrandLogo
        logoUrl={branding.logoUrl}
        logoUrlByLocale={branding.logoUrlByLocale}
        alt={branding.siteName}
        width={LOGO_WIDTH}
        height={LOGO_HEIGHT}
        heightByLocale={LOGO_HEIGHT_BY_LOCALE}
        priority
      />

      <DesktopNav component="nav">
        {content.links.map(link => {
          if (link.key === DEVELOPMENTS_NAV_KEY && showDevelopments && developments) {
            return <DestinationsMenu key={link.key} labelKey={link.labelKey} data={developments} />;
          }
          return <NavItem key={link.key} item={link} />;
        })}
      </DesktopNav>

      <RightSlot>
        <ContactLinks hotline={hotline} whatsappHref={whatsappHref} showText />
        <RegisterInterestCta onClick={trackHeaderRegisterInterestClick} />
        <LocaleSwitcher current={locale} locales={locales} />
      </RightSlot>
    </DesktopBar>
  );
}

HeaderDesktop.displayName = 'HeaderDesktop';
